import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

export const vertexShader = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
`;

export const fragmentShader = `
    uniform sampler2D uTexture;
    varying vec2 vUv;
    void main() {
        vec4 textureColor = texture2D(uTexture, vec2(vUv.x, 0.5 + vUv.y/2.));
        vec4 textureMask = texture2D(uTexture, vec2(vUv.x, vUv.y/2.));
        float alpha = 1.0 - textureMask.x;
        gl_FragColor = vec4(textureColor.xyz, alpha);
    }
`;

interface ITransparentVideoOverlayProps {
  url: string;
  distance?: number;
  height?: number;
  corner?: "center" | "bottom-right";
  marginX?: number;
  marginY?: number;
}

export interface ITransparentVideoOverlayHandle {
  playWithAudio: () => Promise<boolean>;
}

const TransparentVideoOverlay = forwardRef<
  ITransparentVideoOverlayHandle,
  ITransparentVideoOverlayProps
>(function TransparentVideoOverlay(
  {
    url,
    distance = 3.2,
    height = 2.4,
    corner = "center",
    marginX = 0.2,
    marginY = 0.2,
  },
  ref,
) {
  const meshRef = useRef<THREE.Mesh>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const textureRef = useRef<THREE.VideoTexture | null>(null);
  const cameraDirectionRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const cameraRightRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const cameraUpRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const meshPositionRef = useRef<THREE.Vector3>(new THREE.Vector3());

  const [ready, setReady] = useState(false);
  const [visibleAspect, setVisibleAspect] = useState(16 / 9);

  const { camera } = useThree();
  const uniforms = useMemo(
    () => ({
      uTexture: { value: null as THREE.Texture | null },
    }),
    [],
  );

  const ensureVideoTexture = useCallback(
    (video: HTMLVideoElement) => {
      if (textureRef.current) return;

      const texture = new THREE.VideoTexture(video);
      texture.encoding = THREE.sRGBEncoding;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      textureRef.current = texture;
      uniforms.uTexture.value = texture;
    },
    [uniforms],
  );

  const playWithAudio = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return false;

    ensureVideoTexture(video);
    video.muted = false;
    video.defaultMuted = false;

    try {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      setReady(true);
      return true;
    } catch (error) {
      console.warn("Could not enable transparent scene video audio:", error);
      return false;
    }
  }, [ensureVideoTexture]);

  useImperativeHandle(
    ref,
    () => ({
      playWithAudio,
    }),
    [playWithAudio],
  );

  useEffect(() => {
    let cancelled = false;

    setReady(false);

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = false;
    video.preload = "auto";
    video.autoplay = false;
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.src = url;
    videoRef.current = video;

    const handleLoadedMetadata = () => {
      if (cancelled) return;
      if (!video.videoWidth || !video.videoHeight) return;

      const alphaPackedHeight = video.videoHeight / 2;
      if (alphaPackedHeight <= 0) return;

      setVisibleAspect(video.videoWidth / alphaPackedHeight);
    };

    const handleError = () => {
      if (cancelled) return;
      setReady(false);
      console.warn("Transparent scene video failed to load.");
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("error", handleError);

    video.load();

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
      video.pause();
      video.src = "";

      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }

      uniforms.uTexture.value = null;
      videoRef.current = null;
      setReady(false);
    };
  }, [url, uniforms]);

  useFrame(() => {
    const mesh = meshRef.current;
    const width = height * visibleAspect;
    if (mesh) {
      let viewHeight = 2;
      let viewWidth = 2;

      if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
        const perspectiveCamera = camera as THREE.PerspectiveCamera;
        const verticalFovRadians = THREE.MathUtils.degToRad(
          perspectiveCamera.fov,
        );
        viewHeight = 2 * Math.tan(verticalFovRadians / 2) * distance;
        viewWidth = viewHeight * perspectiveCamera.aspect;
      } else if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
        const orthographicCamera = camera as THREE.OrthographicCamera;
        viewHeight = orthographicCamera.top - orthographicCamera.bottom;
        viewWidth = orthographicCamera.right - orthographicCamera.left;
      }

      let horizontalOffset = 0;
      let verticalOffset = 0;

      if (corner === "bottom-right") {
        horizontalOffset = viewWidth / 2 - width / 2 - marginX;
        verticalOffset = -viewHeight / 2 + height / 2 + marginY;
      }

      camera.getWorldDirection(cameraDirectionRef.current);
      cameraRightRef.current
        .set(1, 0, 0)
        .applyQuaternion(camera.quaternion)
        .normalize();
      cameraUpRef.current
        .set(0, 1, 0)
        .applyQuaternion(camera.quaternion)
        .normalize();

      meshPositionRef.current
        .copy(camera.position)
        .addScaledVector(cameraDirectionRef.current, distance)
        .addScaledVector(cameraRightRef.current, horizontalOffset)
        .addScaledVector(cameraUpRef.current, verticalOffset);

      mesh.position.copy(meshPositionRef.current);
      mesh.quaternion.copy(camera.quaternion);
    }

    const video = videoRef.current;
    const texture = textureRef.current;
    if (
      video &&
      texture &&
      video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    ) {
      texture.needsUpdate = true;
    }
  });

  if (!ready || !textureRef.current) return null;

  const width = height * visibleAspect;

  return (
    <mesh ref={meshRef} renderOrder={15}>
      <planeGeometry args={[width, height]} />
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        transparent={true}
        depthWrite={false}
        depthTest={false}
        toneMapped={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
});

export default TransparentVideoOverlay;
